import type { SkillId } from './skills.js';
import type { ItemId } from './items.js';

/**
 * The village board: three small jobs a day and one bigger one a week, picked for each
 * character from the work they can already do. They count themselves as you play (no hand-in)
 * and pay coins and xp the moment they're done. Days and weeks turn over at midnight UTC.
 */
export type BoardJob = {
  id: string;
  text: string;
  skill: SkillId;
  /** Level needed in `skill` before the board offers it. */
  req: number;
  n: number;
  /** What counts: items gained of these ids (or any of this category), fish landed, crops picked, or creatures beaten. */
  count: { items?: ItemId[]; cat?: string; fish?: true; crop?: true; kill?: string[] };
};

export const BOARD_JOBS: BoardJob[] = [
  { id: 'birch', text: 'Cut birch logs', skill: 'forestry', req: 1, n: 20, count: { items: ['birch_log'] } },
  { id: 'pine', text: 'Cut pine logs', skill: 'forestry', req: 10, n: 20, count: { items: ['pine_log'] } },
  { id: 'oak', text: 'Cut oak logs', skill: 'forestry', req: 20, n: 16, count: { items: ['oak_log'] } },
  { id: 'willow', text: 'Cut willow logs', skill: 'forestry', req: 26, n: 16, count: { items: ['willow_log'] } },
  { id: 'copper', text: 'Mine copper or tin ore', skill: 'mining', req: 1, n: 20, count: { items: ['copper_ore', 'tin_ore'] } },
  { id: 'coal', text: 'Mine coal', skill: 'mining', req: 10, n: 16, count: { items: ['coal'] } },
  { id: 'iron', text: 'Mine iron ore', skill: 'mining', req: 15, n: 16, count: { items: ['iron_ore'] } },
  { id: 'fish', text: 'Land fish', skill: 'fishing', req: 1, n: 15, count: { fish: true } },
  { id: 'cook', text: 'Cook food (without burning it)', skill: 'cooking', req: 1, n: 15, count: { cat: 'cooked' } },
  { id: 'bronze', text: 'Smelt bronze bars', skill: 'smithing', req: 1, n: 10, count: { items: ['bronze_bar'] } },
  { id: 'ironbar', text: 'Smelt iron bars', skill: 'smithing', req: 10, n: 10, count: { items: ['iron_bar'] } },
  { id: 'steel', text: 'Smelt steel bars', skill: 'smithing', req: 20, n: 8, count: { items: ['steel_bar'] } },
  { id: 'harvest', text: 'Harvest crops', skill: 'farming', req: 1, n: 10, count: { crop: true } },
  { id: 'pests', text: 'Deal with chickens, rabbits or feral dogs', skill: 'melee', req: 1, n: 10, count: { kill: ['chicken', 'rabbit', 'dog'] } },
  { id: 'boar', text: 'Deal with wild boar', skill: 'melee', req: 10, n: 6, count: { kill: ['boar'] } },
  { id: 'wolf', text: 'Drive off grey wolves', skill: 'melee', req: 20, n: 5, count: { kill: ['wolf'] } },
];
export const BOARD_JOB = Object.fromEntries(BOARD_JOBS.map(j => [j.id, j])) as Record<string, BoardJob>;

/** One job taken from the board: how far along, and whether it has paid out. */
export interface BoardTask { id: string; n: number; need: number; done: boolean }
export interface BoardState { day: number; week: number; daily: BoardTask[]; weekly: BoardTask | null }

export const DAY_MS = 86_400_000;
export const boardDay = (now = Date.now()) => Math.floor(now / DAY_MS);
/** Weeks start on Monday (day 0 of the epoch was a Thursday). */
export const boardWeek = (now = Date.now()) => Math.floor((boardDay(now) + 3) / 7);
/** ms until the next daily and weekly turnover. */
export function boardResets(now = Date.now()) {
  const day = boardDay(now), week = boardWeek(now);
  return { daily: (day + 1) * DAY_MS - now, weekly: ((week + 1) * 7 - 3) * DAY_MS - now };
}

export const DAILY_COINS = 40, DAILY_XP = 120, WEEKLY_COINS = 300, WEEKLY_XP = 900, WEEKLY_MULT = 5;
/** xp scales a little with your level in the job's skill, so the board stays worth doing. */
export const boardXp = (base: number, level: number) => Math.round(base * (1 + level / 25));

function rng(seed: number) { let a = seed | 0; return () => { a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
function hashStr(s: string) { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h | 0; }

/** The jobs a character can do, by level. */
export function boardEligible(levels: Partial<Record<SkillId, number>>) { return BOARD_JOBS.filter(j => (levels[j.skill] ?? 1) >= j.req); }

/** Today's three and this week's one for a character: the same all day for them, different for each person. */
export function rollBoard(who: string, levels: Partial<Record<SkillId, number>>, now = Date.now()): BoardState {
  const day = boardDay(now), week = boardWeek(now), pool = boardEligible(levels);
  const pick = (seed: number, k: number, avoid: string[] = []) => {
    const r = rng(seed), list = pool.filter(j => !avoid.includes(j.id)), out: BoardJob[] = [];
    // one job per skill where possible, so the day is varied
    while (out.length < k && list.length) {
      const i = Math.floor(r() * list.length), j = list.splice(i, 1)[0];
      if (out.some(o => o.skill === j.skill) && list.some(l => !out.some(o => o.skill === l.skill))) continue;
      out.push(j);
    }
    return out;
  };
  const daily = pick(hashStr(`${who}:d${day}`), 3).map(j => ({ id: j.id, n: 0, need: j.n, done: false }));
  const wk = pick(hashStr(`${who}:w${week}`), 1)[0];
  return { day, week, daily, weekly: wk ? { id: wk.id, n: 0, need: wk.n * WEEKLY_MULT, done: false } : null };
}
